import isFunction from 'lodash/isFunction'
import type { FunctionComponent, ReactNode } from 'react'
import React from 'react'
import type { ModalProps, ModalTriggerProps } from '@ztechapps/webui.modal'
import { ModalDragHandle, Modal as ModalCore } from '@ztechapps/webui.modal'
import type { SaveCancelButtonsProps } from '@ztechapps/webui.button'
import { BusyButton, SaveCancelButtons } from '@ztechapps/webui.button'
import { Modal } from 'react-bootstrap'
import type { FormProps } from '../Form'
import { Form } from '../Form'
import { ShowIf } from '@ztechapps/webui.util'

type FormModalProps<TValue = any> = {
  trigger?: FunctionComponent<ModalTriggerProps>
  triggerIcon?: string
  title?: ReactNode
  saveText?: string
  children?: ReactNode
  show?: boolean
  onApply?: any
  saveCancelButtonsProps?: SaveCancelButtonsProps
} & Omit<ModalProps, 'children'> &
  Pick<FormProps<TValue>, 'initialValues' | 'onSubmit' | 'onReset' | 'validate'>
/**
 * This is a component with formik inside but encapsulate it self with modal form
 * it also has the delete, save, cancel button
 * this is the replacement of withFormModal
 */
export function FormModal<TValue = any>({
  size,
  dialogClassName,
  trigger,
  triggerIcon = 'plus',
  title,
  saveText,
  children,
  initialValues,
  onSubmit,
  onReset,
  validate,
  show,
  onApply,
  saveCancelButtonsProps,
}: FormModalProps<TValue>) {
  saveText = saveCancelButtonsProps?.saveText || saveText || 'Add'

  return (
    <ModalCore {...{ triggerIcon, dialogClassName, trigger, size, show }}>
      {({ toggleShowModal }) => (
        <Form<TValue>
          {...{ initialValues, onSubmit, onReset, validate, afterSubmit: toggleShowModal, afterReset: toggleShowModal }}
        >
          {({ isSubmitting, ...rest }) => (
            <React.Fragment>
              <ModalDragHandle>{title}</ModalDragHandle>
              <Modal.Body>{isFunction(children) ? children({ isSubmitting, ...rest }) : children}</Modal.Body>
              <Modal.Footer>
                <div className={'d-flex justify-content-between flex-fill'}>
                  <div>
                    <ShowIf condition={isFunction(onApply)}>
                      <BusyButton
                        bsStyle={'warning'}
                        text={'Apply'}
                        onClick={() => {
                          onApply?.(rest?.values)
                        }}
                      />
                    </ShowIf>
                  </div>

                  <SaveCancelButtons
                    {...(saveCancelButtonsProps ?? {})}
                    saveText={saveText}
                    saveType="submit"
                    cancelType="reset"
                    disabled={rest?.submitCount > 0 && !rest?.isValid}
                    isBusy={isSubmitting}
                  />
                </div>
              </Modal.Footer>
            </React.Fragment>
          )}
        </Form>
      )}
    </ModalCore>
  )
}
